import React from 'react';
import { tokenizeMarkdown, type MarkdownToken } from '../../lib/markdown-tokenizer';

interface CodeBlockRendererProps {
  content: string;
}

/**
 * Render a chat message that may contain fenced code blocks and inline
 * backticks. Uses a state-machine tokenizer (no regex) so it can't be
 * confused by unterminated fences or nested backticks.
 */
export function CodeBlockRenderer({ content }: CodeBlockRendererProps): React.JSX.Element {
  const tokens = tokenizeMarkdown(content);
  return (
    <>
      {tokens.map((token, i) => (
        <Token key={i} token={token} />
      ))}
    </>
  );
}

/** Render one markdown token as the appropriate HTML element. */
function Token({ token }: { token: MarkdownToken }): React.JSX.Element {
  if (token.kind === 'code-block') {
    return (
      <pre
        style={{
          fontFamily: 'monospace',
          fontSize: '11px',
          padding: '8px 10px',
          borderRadius: '6px',
          backgroundColor: 'rgba(0,0,0,0.2)',
          overflow: 'auto',
          margin: '6px 0',
        }}
      >
        {token.value}
      </pre>
    );
  }
  if (token.kind === 'inline-code') {
    return (
      <code
        style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          padding: '1px 5px',
          borderRadius: '3px',
          backgroundColor: 'rgba(0,0,0,0.15)',
        }}
      >
        {token.value}
      </code>
    );
  }
  if (token.kind === 'link') {
    return (
      <a
        href={token.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#01A9ED', textDecoration: 'underline' }}
      >
        {token.text}
      </a>
    );
  }
  return <span>{token.value}</span>;
}
